<template>
  <b-overlay
    :show="pending"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-card>
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex align-items-center mb-50">
          <b-button
            variant="flat-secondary"
            size="sm"
            class="px-50"
            :to="{ name: 'pamm_own_invest' }"
          >
            <feather-icon
              :icon="$i18n.isRTL ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
              size="16"
            />
          </b-button>
          <b-avatar
            :text="initials"
            variant="light-success"
            size="42"
            class="mx-1 flex-shrink-0"
          />
          <div>
            <h4 class="mb-0">
              {{ accountName }}
            </h4>
            <small class="text-muted">#{{ managerId }}</small>
          </div>
        </div>
        <div class="d-flex flex-wrap align-items-center mb-50">
          <b-form-select
            v-if="periodOptions.length > 1"
            v-model="selectedId"
            :options="periodOptions"
            size="sm"
            class="pamm-details__period-select m-25"
            @change="selectInvestment"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            class="m-25"
            :href="`${accountDetailsUrl}/${managerId}`"
            target="_blank"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="14"
            />
            <span class="mx-50">{{ $t('account_page') }}</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <template v-if="selectedId">
      <b-card :title="$t('pamm_overview')">
        <b-row>
          <b-col
            v-for="tile in tiles"
            :key="tile.key"
            cols="6"
            lg
            class="mb-1"
          >
            <div class="pamm-details__tile h-100">
              <small class="text-muted d-block">{{ $t(tile.label) }}</small>
              <h3
                class="font-weight-bolder mb-0 mt-25"
                :class="tile.colored ? valueClass(tile.value) : ''"
              >
                <span dir="ltr">{{ tile.text }}</span>
              </h3>
              <small
                v-if="tile.note"
                :class="tile.noteClass || 'text-muted'"
              >
                <span dir="ltr">{{ tile.note }}</span>
              </small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h4 class="card-title mb-0">
            {{ $t('pamm_current_period') }}
            <span
              v-if="hasValue(periodId)"
              class="text-muted"
            >#{{ periodId }}</span>
          </h4>
          <b-badge :variant="stateVariants[state] || 'light-secondary'">
            {{ $t(`pamm_investment_state_${state}`) }}
          </b-badge>
        </div>
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="mb-1"
          >
            <div class="pamm-details__tile h-100">
              <div class="d-flex align-items-center text-muted mb-50">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="15"
                />
                <span class="mx-50">{{ $t('pamm_trade_window') }}</span>
              </div>
              <span class="font-weight-bolder text-nowrap">
                {{ formatDateTime(tradeStart) }} - {{ formatDateTime(tradeEnd) }}
              </span>
            </div>
          </b-col>
          <b-col
            v-if="exitedAt"
            cols="12"
            md="6"
            class="mb-1"
          >
            <div class="pamm-details__tile h-100">
              <div class="d-flex align-items-center text-muted mb-50">
                <feather-icon
                  icon="LogOutIcon"
                  size="15"
                />
                <span class="mx-50">{{ $t('pamm_exit_date') }}</span>
              </div>
              <span class="font-weight-bolder text-nowrap">{{ formatDateTime(exitedAt) }}</span>
            </div>
          </b-col>
        </b-row>
        <template v-if="tradeProgress">
          <div class="d-flex justify-content-between mb-25">
            <small class="text-muted">{{ $t('pamm_trading_progress') }}</small>
            <small class="font-weight-bold">{{ tradeProgress.label }}</small>
          </div>
          <b-progress
            :value="tradeProgress.percent"
            max="100"
            height="8px"
            variant="success"
          />
        </template>
      </b-card>

      <b-row class="match-height">
        <b-col
          cols="12"
          lg="7"
        >
          <b-card :title="$t('pamm_daily_value')">
            <b-card-text class="text-muted">
              {{ $t('pamm_daily_value_description') }}
            </b-card-text>
            <vue-apex-charts
              v-if="chartSeries[0].data.length > 1"
              type="area"
              height="280"
              :options="chartOptions"
              :series="chartSeries"
            />
            <p
              v-else
              class="text-center text-muted py-2 mb-0"
            >
              {{ $t('pamm_no_daily_data') }}
            </p>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          lg="5"
        >
          <b-card :title="$t('pamm_breakdown')">
            <b-card-text
              v-if="breakdownAsOf"
              class="text-muted"
            >
              {{ $t('pamm_breakdown_as_of', { date: formatDate(breakdownAsOf) }) }}
            </b-card-text>
            <dl class="row mb-0">
              <template v-for="row in breakdown">
                <dt
                  :key="`${row.key}-label`"
                  class="col-7 font-weight-normal text-muted"
                >
                  {{ $t(row.label) }}
                </dt>
                <dd
                  :key="`${row.key}-value`"
                  class="col-5 font-weight-bold text-nowrap"
                  :class="row.colored ? valueClass(row.value) : ''"
                >
                  <span dir="ltr">{{ formatAmount(row.value, row.signed) }}</span>
                </dd>
              </template>
            </dl>
            <div class="pamm-details__tile d-flex align-items-center justify-content-between mt-1">
              <span class="font-weight-bold">{{ $t(valueLabel) }}</span>
              <h4 class="mb-0 font-weight-bolder">
                <span dir="ltr">{{ formatAmount(figures.value) }}</span>
              </h4>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <b-card v-else-if="!pending">
      <p class="text-center text-muted py-2 mb-0">
        {{ $t('pamm_no_investment_data') }}
      </p>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BCardText,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BBadge,
  BProgress,
  BFormSelect,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import jalaali from 'moment-jalaali'
import { $themeColors } from '@themeConfig'
import PammApis from '@/modules/pamm/service/apis'
import {
  field, listOf, recordOf, toDate, toNumber,
} from '@/modules/pamm/utils/response'
import { numberWithCommas } from '@/utils/index'

const pammApis = new PammApis()

const DAY = 24 * 60 * 60 * 1000

export default {
  components: {
    BOverlay,
    BCard,
    BCardText,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BBadge,
    BProgress,
    BFormSelect,
    VueApexCharts,
  },
  data() {
    return {
      accountDetailsUrl: process.env.VUE_APP_PAMM_ACCOUNT_BASE_URL,
      pending: false,
      investments: [],
      // the investor row (pamm_investors.id) shown; one per period
      selectedId: null,
      position: null,
      dailyFund: [],
      stateVariants: {
        trading: 'light-success',
        settling: 'light-warning',
        settled: 'light-secondary',
        exited: 'light-danger',
      },
    }
  },
  computed: {
    managerId() {
      return this.$route.params.id
    },
    accountName() {
      return field(this.selected, 'nick_name') || this.$route.query.name || `#${this.managerId}`
    },
    initials() {
      const name = String(this.accountName || '').replace('#', '').trim()
      return name ? name.slice(0, 2).toUpperCase() : '#'
    },
    // this manager's investments, newest period first
    managerInvestments() {
      return this.investments
        .filter(row => String(field(row, 'pamm_manager_id') ?? field(row, 'manager_id')) === String(this.managerId))
        .sort((a, b) => (toDate(field(b, 'trade_start_at'))?.getTime() ?? 0) - (toDate(field(a, 'trade_start_at'))?.getTime() ?? 0))
    },
    selected() {
      return this.managerInvestments.find(row => String(field(row, 'pamm_investor_id')) === String(this.selectedId)) ?? null
    },
    periodOptions() {
      return this.managerInvestments.map(row => ({
        value: field(row, 'pamm_investor_id'),
        text: `${this.$t('pamm_period')} #${field(row, 'pamm_period_id')} · ${this.$t(`pamm_investment_state_${this.stateOf(row)}`)}`,
      }))
    },
    state() {
      return this.stateOf(this.selected)
    },
    periodId() {
      return field(this.selected, 'pamm_period_id') ?? field(this.position, 'pamm_period_id')
    },
    tradeStart() {
      return field(this.selected, 'trade_start_at') ?? field(this.position, 'trade_start_at')
    },
    tradeEnd() {
      return field(this.selected, 'trade_end_at') ?? field(this.position, 'trade_end_at')
    },
    exitedAt() {
      return field(this.selected, 'exited_at') ?? field(this.position, 'exited_at')
    },
    lastDay() {
      return this.dailyFund.length ? this.dailyFund[this.dailyFund.length - 1] : null
    },
    // While trading the figures are the latest day's; once out of the period, the settlement's.
    figures() {
      const row = this.selected
      const { position, lastDay } = this
      const stake = toNumber(field(position, 'stake') ?? field(row, 'stake'))
      if (this.state === 'trading') {
        return {
          stake,
          value: toNumber(field(position, 'value') ?? field(lastDay, 'net_capital') ?? stake),
          grossShare: toNumber(field(lastDay, 'gross_share')),
          managementFee: toNumber(field(lastDay, 'management_fee')),
          performanceFee: toNumber(field(lastDay, 'performance_fee')),
          fine: null,
        }
      }
      return {
        stake,
        value: toNumber(field(row, 'payout') ?? field(row, 'closing_balance') ?? field(position, 'closing_balance')),
        grossShare: toNumber(field(row, 'gross_share') ?? field(position, 'gross_share')),
        managementFee: toNumber(field(row, 'management_fee') ?? field(position, 'management_fee')),
        performanceFee: toNumber(field(row, 'performance_fee') ?? field(position, 'performance_fee')),
        fine: toNumber(field(row, 'cancelation_fine') ?? field(position, 'cancelation_fine')),
      }
    },
    valueLabel() {
      return this.state === 'trading' ? 'pamm_current_value' : 'pamm_final_amount'
    },
    tiles() {
      const { stake, value } = this.figures
      const profit = value !== null && stake !== null ? value - stake : null
      const participation = toNumber(field(this.position, 'participation_percent') ?? field(this.selected, 'participation_percent'))
      return [
        { key: 'stake', label: 'pamm_invested_amount', text: this.formatAmount(stake) },
        {
          key: 'value',
          label: this.valueLabel,
          text: this.formatAmount(value),
          note: this.state === 'settling' ? this.$t('pamm_investment_state_settling') : null,
          noteClass: 'text-warning',
        },
        {
          key: 'profit',
          label: 'pamm_net_profit',
          text: this.formatAmount(profit, true),
          value: profit,
          colored: true,
          note: profit !== null && stake ? this.formatPercent((profit / stake) * 100) : null,
          noteClass: this.valueClass(profit),
        },
        {
          key: 'participation',
          label: 'pamm_participation',
          text: participation !== null ? `${Math.round(participation * 100) / 100}%` : null,
        },
      ].filter(tile => tile.text !== null)
    },
    breakdown() {
      const {
        stake, grossShare, managementFee, performanceFee, fine,
      } = this.figures
      return [
        { key: 'stake', label: 'pamm_early_exit_stake', value: stake },
        {
          key: 'share', label: 'pamm_share_of_pandl', value: grossShare, signed: true, colored: true,
        },
        {
          key: 'management', label: 'pamm_early_exit_management_fee', value: this.negate(managementFee), signed: true,
        },
        {
          key: 'performance', label: 'pamm_early_exit_performance_fee', value: this.negate(performanceFee), signed: true,
        },
        {
          key: 'fine', label: 'pamm_early_exit_fine', value: fine ? -fine : null, signed: true,
        },
      ].filter(row => row.value !== null && row.value !== undefined && !Number.isNaN(row.value))
    },
    breakdownAsOf() {
      return this.state === 'trading' ? field(this.lastDay, 'business_date') : null
    },
    tradeProgress() {
      if (this.state !== 'trading') return null
      const start = toDate(this.tradeStart)?.getTime()
      const end = toDate(this.tradeEnd)?.getTime()
      if (!start || !end || end <= start) return null
      const now = Date.now()
      if (now < start) {
        return { percent: 0, label: this.$t('pamm_starts_in', { days: Math.ceil((start - now) / DAY) }) }
      }
      if (now >= end) {
        return { percent: 100, label: this.$t('pamm_period_finished') }
      }
      return {
        percent: Math.round(((now - start) / (end - start)) * 100),
        label: this.$t('pamm_days_left', { days: Math.ceil((end - now) / DAY) }),
      }
    },
    chartSeries() {
      return [{
        name: this.$t('pamm_current_value'),
        data: this.dailyFund.map(day => Math.round(toNumber(field(day, 'net_capital')) * 100) / 100),
      }]
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: [$themeColors.success],
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 0.9, opacityFrom: 0.45, opacityTo: 0.05 },
        },
        grid: { borderColor: 'rgba(34, 41, 47, 0.08)' },
        xaxis: {
          categories: this.dailyFund.map(day => this.formatDate(field(day, 'business_date'))),
          labels: { hideOverlappingLabels: true },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: { formatter: value => numberWithCommas(Math.round(value * 100) / 100) },
        },
        tooltip: {
          y: { formatter: value => this.formatAmount(value) },
        },
      }
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    async getData() {
      this.pending = true
      await pammApis.getInvestments().then(res => {
        this.investments = listOf(res)
      }).catch(err => {
        console.log(err)
      })
      const requested = this.$route.query.investor
      const known = this.managerInvestments.some(row => String(field(row, 'pamm_investor_id')) === String(requested))
      // the requested investment, else the newest with this manager; the requested id alone
      // when the list does not have it
      this.selectedId = known ? requested : (field(this.managerInvestments[0], 'pamm_investor_id') ?? requested ?? null)
      await this.getDetails()
      this.pending = false
    },
    async selectInvestment() {
      if (String(this.$route.query.investor) !== String(this.selectedId)) {
        this.$router.replace({ query: { ...this.$route.query, investor: this.selectedId } })
      }
      this.pending = true
      await this.getDetails()
      this.pending = false
    },
    // The daily figures for every investment; the live position only while it trades.
    async getDetails() {
      this.position = null
      this.dailyFund = []
      if (!this.selectedId) return
      const investorId = this.selectedId
      await Promise.all([
        pammApis.getInvestmentDailyFund(investorId).then(res => {
          this.dailyFund = listOf(res)
            .slice()
            .sort((a, b) => (toDate(field(a, 'business_date'))?.getTime() ?? 0) - (toDate(field(b, 'business_date'))?.getTime() ?? 0))
        }).catch(err => {
          console.log(err)
        }),
        this.state === 'trading'
          ? pammApis.getInvestmentPosition(investorId).then(res => {
            this.position = recordOf(res)
          }).catch(err => {
            console.log(err)
          })
          : Promise.resolve(),
      ])
    },
    stateOf(row) {
      if (!row) return 'trading'
      if (field(row, 'exited_at')) return 'exited'
      const payout = field(row, 'payout') ?? field(row, 'closing_balance')
      const status = field(row, 'status')
      if (status === 'settled' || status === 'settling') {
        return payout !== null && payout !== undefined ? 'settled' : 'settling'
      }
      return 'trading'
    },
    negate(value) {
      return value === null || value === undefined ? null : -value
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== ''
    },
    valueClass(value) {
      const number = Number(value)
      if (!value || Number.isNaN(number)) return ''
      if (number > 0) return 'text-success'
      if (number < 0) return 'text-danger'
      return ''
    },
    formatAmount(value, signed = false) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
      // + 0 turns -0 into 0
      const number = Math.round(Number(value) * 100) / 100 + 0
      return `${signed && number > 0 ? '+' : ''}${numberWithCommas(number.toFixed(2))} $`
    },
    formatPercent(value) {
      const rounded = Math.round(value * 100) / 100 + 0
      return `${rounded > 0 ? '+' : ''}${rounded}%`
    },
    formatDate(value) {
      const date = toDate(value)
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD' : 'YYYY-MM-DD')
    },
    formatDateTime(value) {
      const date = toDate(value)
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD HH:mm' : 'YYYY-MM-DD HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.pamm-details__tile {
  padding: 0.75rem 1rem;
  border-radius: 0.428rem;
  background-color: rgba(34, 41, 47, 0.03);
}

.pamm-details__period-select {
  width: auto;
  min-width: 13rem;
}

.dark-layout .pamm-details__tile {
  background-color: rgba(255, 255, 255, 0.04);
}
</style>
